import type { CreateExpenseDto, PendingExpense } from '@splitfiesta/shared';

import {
  convertAmount,
  isPinUsable,
  settleDrift,
} from '../expense/ConversionService';
import { toCreateExpenseDto } from './pendingExpenseDto';

/**
 * Converts a queued foreign-currency `PendingExpense` into a postable
 * `CreateExpenseDto` using a freshly-fetched exchange rate.
 *
 * Pure — no I/O, no store access. The offline queue stores this kind
 * of expense in its ORIGINAL currency (`pendingConversion: true`,
 * `targetCurrency` = the group currency) rather than trusting a rate
 * captured while offline; `runOfflineSync.syncOne` fetches a live
 * rate via `getExchangeRate` at sync time and calls this right before
 * POST — see that file for the caller and its non-transient/transient
 * failure handling around the rate lookup itself.
 *
 * - `amount` converts at `rate.rate` (`convertAmount`, rounded to the
 *   target currency); each payer/split converts unrounded and
 *   `settleDrift` apportions the total by largest remainder, so both sets
 *   sum exactly to it and no item drops below one minor unit.
 * - `originalAmount`/`originalCurrency` capture the pre-conversion
 *   values for the expense detail view.
 * - `rateDate` is included only when it falls within the server's
 *   six-day pin window for `pending.date` (`isPinUsable`); otherwise
 *   the server resolves the rate live rather than trusting a stale
 *   pin.
 * - `pendingConversion`/`targetCurrency` are dropped — the returned
 *   DTO is a normal (already-converted) create payload.
 */
export function resolvePendingConversion(
  pending: PendingExpense,
  rate: { rate: number; rateDate: string }
): CreateExpenseDto {
  const target = pending.targetCurrency ?? pending.currency;
  // The fresh rate replaces whatever an earlier conversion left on the row.
  const {
    exchangeRate: _staleExchangeRate,
    rateDate: _staleRateDate,
    ...base
  } = toCreateExpenseDto(pending);

  const amount = convertAmount(pending.amount, rate.rate, target);
  const payers = settleDrift(
    pending.payers.map(p => ({
      ...p,
      amount: p.amount * rate.rate,
    })),
    amount,
    target
  );
  const splits = settleDrift(
    pending.splits.map(s => ({
      ...s,
      amount: s.amount * rate.rate,
    })),
    amount,
    target
  );

  return {
    ...base,
    amount,
    currency: target,
    payers,
    splits,
    originalAmount: pending.amount,
    originalCurrency: pending.currency,
    ...(isPinUsable(rate.rateDate, pending.date)
      ? { rateDate: rate.rateDate }
      : {}),
  };
}
